import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ConflictError } from '../api/client';
import type { AdminRole, AdminUser, CapabilityMatrix, EmployeeRosterRow, RoleDetail } from '../api/types';
import { useApp } from '../state/AppContext';
import { ConflictBanner, EmployeePicker, EmptyRow, ErrorBanner } from '../components/ui';

/**
 * Who can sign in, and what each person's access is. Before this screen, adding
 * a person or changing a role meant a hand-written SQL statement.
 *
 * Access is a list of assignments rather than one role: a role at one location,
 * or at all of them. Someone covering two plants has two assignments, and a
 * capability applies only where the role granting it was assigned. Assignments
 * are added and removed, never edited, so each change stands on its own in the
 * history.
 */
export function Administration() {
  const { reference, can } = useApp();
  const queryClient = useQueryClient();
  const canEdit = can('users', 'edit');
  // The nav hides this screen, but the URL is still typeable. Without this the
  // list 403s and the empty state claims nobody is set up — telling someone the
  // system is empty when the truth is they lack access.
  const canView = can('users', 'view');

  const [tab, setTab] = useState<'people' | 'roles'>('people');
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [openUpn, setOpenUpn] = useState<string | null>(null);

  const [newUpn, setNewUpn] = useState('');
  const [newName, setNewName] = useState('');
  const [newEmployeeId, setNewEmployeeId] = useState<number | null>(null);
  const [newRole, setNewRole] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [pickerKey, setPickerKey] = useState(0);

  const users = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get<{ rows: AdminUser[] }>('/api/admin/users'),
    enabled: canView,
  });
  const roles = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => api.get<AdminRole[]>('/api/admin/roles'),
    enabled: canView,
  });
  const roster = useQuery({
    queryKey: ['admin-roster'],
    queryFn: () => api.get<{ rows: EmployeeRosterRow[] }>('/api/employees', { take: 5000 }),
    enabled: canView,
  });

  const flash = (message: string) => {
    setNotice(message);
    setTimeout(() => setNotice(null), 5000);
  };
  const handleError = (e: Error) => (e instanceof ConflictError ? setConflict(e.message) : setError(e.message));
  // Changing access changes what the signed-in person can see, so nothing is
  // safe to keep cached — including /api/me.
  const refresh = () => queryClient.invalidateQueries();

  const addUser = useMutation({
    mutationFn: () =>
      api.post<{ userPrincipalName: string }>('/api/admin/users', {
        userPrincipalName: newUpn, displayName: newName || null, employeeId: newEmployeeId,
        roleKey: newRole || null, locationCode: newLocation || null,
      }),
    onSuccess: (r) => {
      // Read the role before clearing the form; these setters do not change
      // the values this closure captured, but the message should be built
      // from what was actually sent.
      const granted = roleList.find((x) => x.roleKey === newRole);
      const where = newLocation || 'all locations';
      setNewUpn(''); setNewName(''); setNewEmployeeId(null); setNewRole(''); setNewLocation('');
      setPickerKey((k) => k + 1);
      refresh();
      if (granted) {
        flash(`${r.userPrincipalName} added as ${granted.displayName} at ${where}.`);
      } else {
        // No role means no access, so open their row: the next thing to do is
        // give them one, and the panel is where that happens.
        setOpenUpn(r.userPrincipalName);
        flash(`${r.userPrincipalName} added with no access yet. Give them a role below.`);
      }
    },
    onError: handleError,
  });

  const saveUser = useMutation({
    mutationFn: (u: AdminUser & { nextName: string; nextEmployeeId: number | null; nextActive: boolean }) =>
      api.put(`/api/admin/users/${encodeURIComponent(u.userPrincipalName)}`, {
        displayName: u.nextName || null, employeeId: u.nextEmployeeId,
        isActive: u.nextActive, rowVersionNo: u.rowVersionNo,
      }),
    onSuccess: (_r, u) => { refresh(); flash(`${u.userPrincipalName} updated.`); },
    onError: handleError,
  });

  const removeUser = useMutation({
    mutationFn: (u: AdminUser) => api.del(`/api/admin/users/${encodeURIComponent(u.userPrincipalName)}`),
    onSuccess: (_r, u) => { setOpenUpn(null); refresh(); flash(`${u.userPrincipalName} removed.`); },
    onError: handleError,
  });

  const addAssignment = useMutation({
    mutationFn: (v: { upn: string; roleKey: string; locationCode: string; title: string }) =>
      api.post(`/api/admin/users/${encodeURIComponent(v.upn)}/assignments`, {
        roleKey: v.roleKey, locationCode: v.locationCode || null, title: v.title || null,
      }),
    onSuccess: () => { refresh(); flash('Access added.'); },
    onError: handleError,
  });

  const removeAssignment = useMutation({
    mutationFn: (assignmentId: number) => api.del(`/api/admin/assignments/${assignmentId}`),
    onSuccess: () => { refresh(); flash('Access removed.'); },
    onError: handleError,
  });

  const rows = users.data?.rows ?? [];
  const roleList = useMemo(() => roles.data ?? [], [roles.data]);
  const people = roster.data?.rows ?? [];
  const picked = people.find((p) => p.employeeId === newEmployeeId);

  // Picking someone fills in what we already hold about them. Only company
  // addresses are imported from the HR export, so most people have none yet and
  // the login still has to be typed; the hint below says which case this is.
  const chooseEmployee = (employeeId: number | null) => {
    setNewEmployeeId(employeeId);
    const person = people.find((p) => p.employeeId === employeeId);
    if (!person) return;
    setNewName(person.sortName || person.displayName);
    if (person.email) setNewUpn(person.email);
  };

  // After every hook has run, never before one.
  if (!canView) {
    return (
      <div>
        <div className="page-header">
          <div><div className="page-title">Administration</div></div>
        </div>
        <ErrorBanner message="Administration is for administrators. Ask one if you need access to it." />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Administration</div>
          <div className="page-subtitle">
            Who can sign in, and what each person can see and do. Access is a role at one location, or at all of
            them — someone covering two plants gets two.
          </div>
        </div>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {conflict && <ConflictBanner message={conflict} onReload={() => { setConflict(null); refresh(); }} />}

      {/* Two jobs live here — who the people are, and what the roles mean.
          Tabs rather than a fourth stacked block, so neither buries the other. */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
        {(['people', 'roles'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className="btn btn--sm"
            style={{
              border: 'none', background: 'none', borderRadius: 0, paddingBottom: 8,
              fontWeight: tab === t ? 700 : 500,
              color: tab === t ? 'var(--blue-deep)' : 'var(--muted)',
              borderBottom: tab === t ? '2px solid var(--blue)' : '2px solid transparent',
            }}
          >
            {t === 'people' ? 'People' : 'Roles'}
          </button>
        ))}
      </div>

      {tab === 'people' && (
        <>
      {canEdit && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="section-label" style={{ marginBottom: 10 }}>ADD A PERSON</div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 300 }}>
              <label className="field-label" htmlFor="adm-employee">FIND THEM IN THE EMPLOYEE LIST</label>
              <EmployeePicker
                key={pickerKey}
                id="adm-employee"
                people={people}
                value={newEmployeeId}
                onChange={chooseEmployee}
              />
            </div>
            <div style={{ minWidth: 250 }}>
              <label className="field-label" htmlFor="adm-upn">WORK EMAIL (SIGN-IN)</label>
              <input
                id="adm-upn"
                className="control"
                style={{ width: '100%' }}
                placeholder="name@cyntox.com"
                value={newUpn}
                onChange={(e) => setNewUpn(e.target.value)}
              />
            </div>
            <div style={{ minWidth: 175 }}>
              <label className="field-label" htmlFor="adm-name">NAME</label>
              <input
                id="adm-name"
                className="control"
                style={{ width: '100%' }}
                placeholder="Ricciardi, Marsha"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div style={{ minWidth: 150 }}>
              <label className="field-label" htmlFor="adm-role">ROLE</label>
              <select
                id="adm-role"
                className="control"
                style={{ width: '100%' }}
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
              >
                <option value="">No access yet</option>
                {roleList.map((r) => <option key={r.roleKey} value={r.roleKey}>{r.displayName}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 150 }}>
              <label className="field-label" htmlFor="adm-location">LOCATION</label>
              <select
                id="adm-location"
                className="control"
                style={{ width: '100%' }}
                value={newLocation}
                disabled={!newRole}
                onChange={(e) => setNewLocation(e.target.value)}
              >
                <option value="">All locations</option>
                {reference.facilities.map((fac) => <option key={fac.code} value={fac.code}>{fac.name}</option>)}
              </select>
            </div>
            <button
              className="btn btn--primary"
              style={{ marginTop: 22 }}
              disabled={!newUpn.trim() || addUser.isPending}
              onClick={() => { setError(null); addUser.mutate(); }}
            >
              {addUser.isPending ? 'Adding…' : 'Add person'}
            </button>
          </div>
          <div className="meta" style={{ marginTop: 8 }}>
            {picked
              ? picked.email
                ? `${picked.displayName}'s work address came from the employee list. Choose a role and they can use the app straight away.`
                : `${picked.displayName} has no company address in the HR export yet, so type the one they sign in with. Personal addresses are never imported.`
              : 'Find someone in the employee list to fill in their name and work address, or type a login directly. Leave the role as "No access yet" to add them without any.'}
          </div>
        </div>
      )}

      <div className="section-label" style={{ marginBottom: 2 }}>PEOPLE</div>
      <div className="meta" style={{ marginBottom: 10 }}>
        {rows.length} {rows.length === 1 ? 'person' : 'people'} · {rows.filter((r) => r.isActive).length} active
      </div>
      {notice && <div className="pill pill--good" style={{ display: 'inline-block', marginBottom: 10 }}>{notice}</div>}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>NAME</th>
              <th>SIGN-IN</th>
              <th>EMPLOYEE</th>
              <th>ACCESS</th>
              <th>STATUS</th>
              {canEdit && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {users.isLoading && <EmptyRow colSpan={canEdit ? 6 : 5}>Loading…</EmptyRow>}
            {users.isError && (
              <EmptyRow colSpan={canEdit ? 6 : 5}>{(users.error as Error).message}</EmptyRow>
            )}
            {!users.isLoading && !users.isError && rows.length === 0 && (
              <EmptyRow colSpan={canEdit ? 6 : 5}>Nobody is set up yet.</EmptyRow>
            )}
            {rows.map((u) => (
              <UserRow
                key={u.userPrincipalName}
                user={u}
                roles={roleList}
                facilities={reference.facilities}
                people={people}
                canEdit={canEdit}
                open={openUpn === u.userPrincipalName}
                onToggle={() => setOpenUpn(openUpn === u.userPrincipalName ? null : u.userPrincipalName)}
                onSave={(nextName, nextEmployeeId, nextActive) =>
                  saveUser.mutate({ ...u, nextName, nextEmployeeId, nextActive })}
                onRemove={() => {
                  if (window.confirm(
                    `Remove ${u.userPrincipalName}?\n\nThey lose access immediately and their roles go with them. `
                    + 'To keep the record, set them inactive instead.')) removeUser.mutate(u);
                }}
                onAddAssignment={(roleKey, locationCode, title) =>
                  addAssignment.mutate({ upn: u.userPrincipalName, roleKey, locationCode, title })}
                onRemoveAssignment={(id) => removeAssignment.mutate(id)}
              />
            ))}
          </tbody>
        </table>
      </div>
        </>
      )}

      {tab === 'roles' && <RolesTab canEdit={canEdit} onError={handleError} />}
    </div>
  );
}

/**
 * What each role means. One role at a time rather than a grid: the question
 * people actually ask is "what can Beth do?", not "who can approve?".
 *
 * The list of capabilities comes from the API, because it is the list of checks
 * the code actually makes. A tick that saved but changed nothing would be worse
 * than a missing one.
 */
function RolesTab({ canEdit, onError }: { canEdit: boolean; onError: (e: Error) => void }) {
  const { me } = useApp();
  const queryClient = useQueryClient();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const matrix = useQuery({
    queryKey: ['admin-capabilities'],
    queryFn: () => api.get<CapabilityMatrix>('/api/admin/capabilities'),
  });

  // Changing a role changes what the signed-in person may see, so nothing is
  // safe to keep cached — including /api/me.
  const refresh = () => queryClient.invalidateQueries();

  const toggle = useMutation({
    mutationFn: (v: { roleKey: string; resource: string; permission: string; allowed: boolean }) =>
      api.put('/api/admin/capabilities', v),
    onSuccess: () => refresh(),
    onError,
  });

  const saveRole = useMutation({
    mutationFn: (v: { role: RoleDetail; displayName: string; description: string; isActive: boolean }) =>
      api.put(`/api/admin/roles/${encodeURIComponent(v.role.roleKey)}`, {
        displayName: v.displayName,
        description: v.description || null,
        sortOrder: v.role.sortOrder,
        isActive: v.isActive,
        rowVersionNo: v.role.rowVersionNo,
      }),
    onSuccess: () => refresh(),
    onError,
  });

  const createRole = useMutation({
    mutationFn: () =>
      api.post<{ roleKey: string }>('/api/admin/roles', {
        roleKey: newKey, displayName: newName, description: newDescription || null,
      }),
    onSuccess: (r) => {
      setCreating(false); setNewKey(''); setNewName(''); setNewDescription('');
      setSelectedKey(r.roleKey);
      refresh();
    },
    onError,
  });

  const roles = matrix.data?.roles ?? [];
  const catalogue = matrix.data?.catalogue ?? [];
  const granted = useMemo(() => new Set(matrix.data?.granted ?? []), [matrix.data]);
  const selected = roles.find((r) => r.roleKey === selectedKey) ?? roles[0];
  const mine = selected ? me.assignments.some((a) => a.roleKey === selected.roleKey) : false;

  // Catalogue order is deliberate — it follows the menu — so group without sorting.
  const groups = useMemo(() => {
    const out: { group: string; entries: typeof catalogue }[] = [];
    for (const entry of catalogue) {
      const last = out[out.length - 1];
      if (last && last.group === entry.group) last.entries.push(entry);
      else out.push({ group: entry.group, entries: [entry] });
    }
    return out;
  }, [catalogue]);

  if (matrix.isLoading) return <div className="spinner-note">Loading roles…</div>;
  if (matrix.isError) return <ErrorBanner message={(matrix.error as Error).message} />;

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ minWidth: 240 }}>
        <div className="section-label" style={{ marginBottom: 8 }}>ROLES</div>
        {roles.map((r) => (
          <button
            key={r.roleKey}
            onClick={() => { setSelectedKey(r.roleKey); setCreating(false); }}
            className="btn btn--sm"
            style={{
              display: 'block', width: '100%', textAlign: 'left', marginBottom: 4,
              background: selected?.roleKey === r.roleKey && !creating ? 'var(--blue-tint)' : 'none',
              color: r.isActive ? undefined : 'var(--muted)',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ fontWeight: 600 }}>{r.displayName}{r.isActive ? '' : ' (inactive)'}</div>
            <div className="meta">
              {r.peopleCount} {r.peopleCount === 1 ? 'person' : 'people'}
            </div>
          </button>
        ))}
        {canEdit && (
          <button className="btn btn--sm" style={{ width: '100%', marginTop: 8 }}
            onClick={() => { setCreating(true); }}>
            + New role
          </button>
        )}
      </div>

      {creating && canEdit && (
        <div className="card" style={{ minWidth: 380, flex: 1 }}>
          <div className="section-label" style={{ marginBottom: 10 }}>NEW ROLE</div>
          <label className="field-label" htmlFor="role-name">NAME</label>
          <input id="role-name" className="control" style={{ width: '100%', marginBottom: 8 }}
            placeholder="Shift Lead" value={newName} onChange={(e) => setNewName(e.target.value)} />

          <label className="field-label" htmlFor="role-key">KEY</label>
          <input id="role-key" className="control" style={{ width: '100%' }}
            placeholder="shiftlead" value={newKey}
            onChange={(e) => setNewKey(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))} />
          <div className="meta" style={{ marginTop: 4, marginBottom: 8 }}>
            Letters and digits, and it never changes. Nobody sees it — the name above is what people read.
          </div>

          <label className="field-label" htmlFor="role-desc">DESCRIPTION</label>
          <input id="role-desc" className="control" style={{ width: '100%', marginBottom: 10 }}
            placeholder="What this role is for" value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)} />

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn--primary btn--sm"
              disabled={!newName.trim() || newKey.length < 2 || createRole.isPending}
              onClick={() => createRole.mutate()}>
              {createRole.isPending ? 'Creating…' : 'Create role'}
            </button>
            <button className="btn btn--sm" onClick={() => setCreating(false)}>Cancel</button>
          </div>
          <div className="meta" style={{ marginTop: 8 }}>
            A new role grants nothing until you tick capabilities on it.
          </div>
        </div>
      )}

      {!creating && selected && (
        <div style={{ minWidth: 420, flex: 1 }}>
          <RoleDetailPanel
            key={selected.roleKey}
            role={selected}
            canEdit={canEdit}
            heldByYou={mine}
            groups={groups}
            granted={granted}
            onToggle={(resource, permission, allowed) =>
              toggle.mutate({ roleKey: selected.roleKey, resource, permission, allowed })}
            onSave={(displayName, description, isActive) =>
              saveRole.mutate({ role: selected, displayName, description, isActive })}
          />
        </div>
      )}
    </div>
  );
}

function RoleDetailPanel({
  role, canEdit, heldByYou, groups, granted, onToggle, onSave,
}: {
  role: RoleDetail;
  canEdit: boolean;
  heldByYou: boolean;
  groups: { group: string; entries: { resource: string; permission: string; label: string; detail: string }[] }[];
  granted: Set<string>;
  onToggle: (resource: string, permission: string, allowed: boolean) => void;
  onSave: (displayName: string, description: string, isActive: boolean) => void;
}) {
  const [name, setName] = useState(role.displayName);
  const [description, setDescription] = useState(role.description ?? '');
  const [active, setActive] = useState(role.isActive);

  const dirty = name !== role.displayName
    || description !== (role.description ?? '')
    || active !== role.isActive;

  return (
    <div className="card">
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ minWidth: 220, flex: 1 }}>
          <label className="field-label" htmlFor={`rn-${role.roleKey}`}>NAME</label>
          <input id={`rn-${role.roleKey}`} className="control" style={{ width: '100%' }}
            value={name} disabled={!canEdit} onChange={(e) => setName(e.target.value)} />
        </div>
        <div style={{ minWidth: 260, flex: 2 }}>
          <label className="field-label" htmlFor={`rd-${role.roleKey}`}>DESCRIPTION</label>
          <input id={`rd-${role.roleKey}`} className="control" style={{ width: '100%' }}
            value={description} disabled={!canEdit} onChange={(e) => setDescription(e.target.value)} />
        </div>
        {canEdit && (
          <button className="btn btn--primary btn--sm" disabled={!dirty}
            onClick={() => onSave(name, description, active)}>
            Save
          </button>
        )}
      </div>

      <div className="meta" style={{ marginTop: 8 }}>
        Held by {role.peopleCount} {role.peopleCount === 1 ? 'person' : 'people'}
        {role.assignmentCount !== role.peopleCount ? ` across ${role.assignmentCount} assignments` : ''}.
        {heldByYou ? ' You hold this role — changes below affect what you can do.' : ''}
      </div>

      {canEdit && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, marginTop: 8 }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active — an inactive role grants nothing to anyone holding it
        </label>
      )}

      <div className="section-label" style={{ margin: '16px 0 4px' }}>WHAT THIS ROLE CAN DO</div>
      <div className="meta" style={{ marginBottom: 10 }}>
        Each change saves on its own, and takes effect the next time that person loads a page.
      </div>

      {groups.map((g) => (
        <div key={g.group} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{g.group}</div>
          {g.entries.map((e) => {
            const on = granted.has(`${role.roleKey}|${e.resource}|${e.permission}`);
            return (
              <label key={`${e.resource}|${e.permission}`}
                style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '3px 0' }}>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!canEdit}
                  style={{ marginTop: 3 }}
                  onChange={(ev) => onToggle(e.resource, e.permission, ev.target.checked)}
                />
                <span style={{ fontSize: 12.5 }}>
                  {e.label}
                  <span className="meta" style={{ display: 'block' }}>{e.detail}</span>
                </span>
              </label>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function UserRow({
  user, roles, facilities, people, canEdit, open, onToggle, onSave, onRemove, onAddAssignment, onRemoveAssignment,
}: {
  user: AdminUser;
  roles: AdminRole[];
  facilities: { code: string; name: string }[];
  people: EmployeeRosterRow[];
  canEdit: boolean;
  open: boolean;
  onToggle: () => void;
  onSave: (name: string, employeeId: number | null, active: boolean) => void;
  onRemove: () => void;
  onAddAssignment: (roleKey: string, locationCode: string, title: string) => void;
  onRemoveAssignment: (assignmentId: number) => void;
}) {
  const [name, setName] = useState(user.displayName ?? '');
  const [employeeId, setEmployeeId] = useState<number | null>(user.employeeId ?? null);
  const [active, setActive] = useState(user.isActive);
  const [roleKey, setRoleKey] = useState('');
  const [locationCode, setLocationCode] = useState('');
  const [title, setTitle] = useState('');

  return (
    <>
      <tr style={!user.isActive ? { color: 'var(--muted)' } : undefined}>
        <td className="strong">{user.displayName ?? '—'}</td>
        <td>{user.userPrincipalName}</td>
        <td>{user.employeeName ? `${user.employeeName} (${user.employeeId})` : '—'}</td>
        <td>
          {user.assignments.length === 0
            ? <span className="pill" style={{ background: 'var(--orange-tint)', color: 'var(--orange-deep)' }}>No access yet</span>
            : user.assignments.map((a) => (
                <span key={a.assignmentId} className="pill" style={{ background: 'var(--blue-tint)', color: 'var(--blue-deep)', marginRight: 4 }}>
                  {a.roleName} · {a.locationCode ?? 'all locations'}{a.title ? ` · ${a.title}` : ''}
                </span>
              ))}
        </td>
        <td>{user.isActive ? 'Active' : 'Inactive'}</td>
        {canEdit && (
          <td className="num" style={{ whiteSpace: 'nowrap' }}>
            <button className="btn btn--sm" onClick={onToggle}>{open ? 'Close' : 'Manage'}</button>
          </td>
        )}
      </tr>

      {open && canEdit && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--surface-muted)' }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', padding: '4px 0 10px' }}>
              <div style={{ minWidth: 300 }}>
                <div className="section-label" style={{ marginBottom: 8 }}>DETAILS</div>
                <label className="field-label" htmlFor={`n-${user.userPrincipalName}`}>NAME</label>
                <input id={`n-${user.userPrincipalName}`} className="control" style={{ width: '100%', marginBottom: 8 }}
                  value={name} onChange={(e) => setName(e.target.value)} />

                <label className="field-label" htmlFor={`e-${user.userPrincipalName}`}>EMPLOYEE RECORD</label>
                <EmployeePicker id={`e-${user.userPrincipalName}`} people={people} value={employeeId} onChange={setEmployeeId} />
                <div className="meta" style={{ marginTop: 4, marginBottom: 8 }}>
                  Needed only for a Team Member, so the app knows whose scorecard is theirs.
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                  <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                  Active — an inactive person cannot sign in
                </label>

                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn btn--primary btn--sm" onClick={() => onSave(name, employeeId, active)}>Save details</button>
                  <button className="btn btn--sm btn--reject" onClick={onRemove}>Remove person</button>
                </div>
              </div>

              <div style={{ minWidth: 380, flex: 1 }}>
                <div className="section-label" style={{ marginBottom: 8 }}>ACCESS</div>
                {user.assignments.length === 0 && (
                  <div className="meta" style={{ marginBottom: 8 }}>None yet — they can sign in but see nothing.</div>
                )}
                {user.assignments.map((a) => (
                  <div key={a.assignmentId} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 12.5, minWidth: 260 }}>
                      <strong>{a.roleName}</strong> · {a.locationCode ?? 'all locations'}{a.title ? ` · ${a.title}` : ''}
                    </span>
                    <button className="btn btn--sm btn--reject" onClick={() => onRemoveAssignment(a.assignmentId)}>Remove</button>
                  </div>
                ))}

                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
                  <div>
                    <label className="field-label" htmlFor={`r-${user.userPrincipalName}`}>ROLE</label>
                    <select id={`r-${user.userPrincipalName}`} className="control" value={roleKey}
                      onChange={(e) => setRoleKey(e.target.value)}>
                      <option value="">Choose…</option>
                      {roles.map((r) => <option key={r.roleKey} value={r.roleKey}>{r.displayName}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label" htmlFor={`l-${user.userPrincipalName}`}>LOCATION</label>
                    <select id={`l-${user.userPrincipalName}`} className="control" value={locationCode}
                      onChange={(e) => setLocationCode(e.target.value)}>
                      <option value="">All locations</option>
                      {facilities.map((f) => <option key={f.code} value={f.code}>{f.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label" htmlFor={`t-${user.userPrincipalName}`}>TITLE (OPTIONAL)</label>
                    <input id={`t-${user.userPrincipalName}`} className="control" placeholder="Laborer"
                      value={title} onChange={(e) => setTitle(e.target.value)} />
                  </div>
                  <button className="btn btn--sm" disabled={!roleKey}
                    onClick={() => { onAddAssignment(roleKey, locationCode, title); setRoleKey(''); setTitle(''); }}>
                    Add access
                  </button>
                </div>
                <div className="meta" style={{ marginTop: 8 }}>
                  A role applies only where it is assigned. For two plants, add it twice.
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
